<template>
  <div class="card-wrap">
    <h3 class="title">¿Qué tipo de información vas a importar?</h3>
    <p class="sub">
      Selecciona el módulo destino. El orden recomendado es Catálogo →
      Directorio → Facturas.
    </p>

    <div class="type-grid">
      <div
        v-for="t in types"
        :key="t.id"
        :class="['type-card', { selected: selected === t.id }]"
        @click="$emit('select', t.id)"
      >
        <div class="type-icon" :style="{ background: t.bg }">
          <i :class="t.icon" :style="{ color: t.color }"></i>
        </div>
        <div class="type-name">{{ t.name }}</div>
        <div class="type-desc">{{ t.desc }}</div>
        <div
          class="type-badge"
          :style="{ background: t.badgeBg, color: t.badgeColor }"
        >
          <span class="dot" :style="{ background: t.badgeColor }"></span>
          {{ t.badge }}
        </div>
        <div class="formats">
          <span v-for="f in t.formats" :key="f" class="pill">{{ f }}</span>
        </div>
      </div>
    </div>

    <div class="notice">
      <i class="el-icon-info"></i>
      <span>
        Puedes importar múltiples veces — el sistema detecta duplicados por
        código/RUC y permite actualizar registros existentes.
      </span>
    </div>

    <div class="btn-row">
      <span class="hint">
        Las facturas se guardan como histórico de referencia y aparecen en
        Reportes para comparar ventas.
      </span>
      <el-button type="primary" :disabled="!selected" @click="$emit('next')">
        Continuar →
      </el-button>
    </div>
  </div>
</template>

<script>
export default {
  name: "StepType",
  props: {
    selected: { type: String, default: null }
  },
  data() {
    return {
      types: [
        {
          id: "catalog",
          name: "Catálogo",
          icon: "el-icon-goods",
          color: "#534ab7",
          bg: "#eeedfe",
          desc:
            "Productos y servicios. Se importa primero porque es referenciado por las facturas.",
          formats: ["CSV", "Excel", "TXT"],
          badge: "Recomendado primero",
          badgeColor: "#534ab7",
          badgeBg: "#eeedfe"
        },
        {
          id: "directory",
          name: "Directorio",
          icon: "el-icon-user",
          color: "#1d9e75",
          bg: "#e1f5ee",
          desc:
            "Clientes y empresas con RUC/cédula, contacto, dirección y razón social.",
          formats: ["CSV", "Excel", "TXT"],
          badge: "Antes de facturas",
          badgeColor: "#1d9e75",
          badgeBg: "#e1f5ee"
        },
        {
          id: "invoices",
          name: "Facturas",
          icon: "el-icon-document",
          color: "#ba7517",
          bg: "#faeeda",
          desc:
            "Facturas históricas para reportería. No se firman ni se reenvían al SRI.",
          formats: ["CSV", "Excel", "TXT SRI", "XML SRI"],
          badge: "Histórico de referencia",
          badgeColor: "#ba7517",
          badgeBg: "#faeeda"
        }
      ]
    };
  }
};
</script>

<style lang="scss" scoped>
.card-wrap {
  background: #fff;
  border: 0.5px solid #e4eaf6;
  border-radius: 12px;
  padding: 1.75rem;
}

.title {
  font-size: 17px;
  font-weight: 600;
  color: #2d2b55;
  margin: 0 0 4px;
}

.sub {
  font-size: 13px;
  color: #6e7484;
  margin: 0 0 1.5rem;
}

.type-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.type-card {
  border: 1.5px solid #e4eaf6;
  border-radius: 10px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
  background: #fff;

  &:hover {
    border-color: #3ecfb4;
    background: #f0fdf9;
  }

  &.selected {
    border-color: #3ecfb4;
    background: #f0fdf9;
    box-shadow: 0 0 0 3px rgba(62, 207, 180, 0.12);
  }
}

.type-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  font-size: 20px;
}

.type-name {
  font-size: 15px;
  font-weight: 600;
  color: #2d2b55;
  margin-bottom: 4px;
}

.type-desc {
  font-size: 12px;
  color: #6e7484;
  line-height: 1.5;
  margin-bottom: 10px;
  min-height: 54px;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  padding: 3px 9px;
  border-radius: 20px;
  margin-bottom: 8px;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;
  }
}

.formats {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;

  .pill {
    font-size: 11px;
    padding: 2px 9px;
    border-radius: 20px;
    background: #f7f7f5;
    color: #6e7484;
    border: 0.5px solid #e4eaf6;
  }
}

.notice {
  margin-top: 1.25rem;
  padding: 12px 14px;
  background: #e1f5ee;
  border-radius: 8px;
  font-size: 12px;
  color: #0f6e56;
  display: flex;
  gap: 8px;
  align-items: flex-start;

  i {
    font-size: 14px;
    margin-top: 1px;
  }
}

.btn-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.5rem;
  gap: 12px;
  flex-wrap: wrap;

  .hint {
    font-size: 12px;
    color: #6e7484;
    flex: 1;
    min-width: 200px;
  }
}
</style>
